import { useCallback, useEffect, useState } from "react";
import { ScrollText, Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const ago = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/audit-logs");
      setLogs(data.logs);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = logs.filter((l) => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return [l.action, l.admin_email, l.target_type, l.target_id].some((v) => (v || "").toLowerCase().includes(t));
  });

  if (denied) return <p className="text-sm text-[#71717A]" data-testid="admin-audit-denied">You don't have the "audit.view" permission.</p>;
  if (loading) return <div className="flex justify-center py-20 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div data-testid="admin-audit">
      <div className="flex items-center gap-2 mb-4">
        <ScrollText className="w-5 h-5 text-[#D4AF37]" />
        <h3 className="text-white font-medium">Audit log</h3>
        <span className="text-xs text-[#52525B]">({filtered.length})</span>
      </div>
      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 text-[#71717A] absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search actions, admin, target…" data-testid="admin-audit-search"
          className="bg-[#121214] border-white/10 text-white pl-9 h-10" />
      </div>
      <div className="rounded-2xl border border-white/8 bg-[#121214] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-audit-table">
            <thead><tr className="border-b border-white/8 text-left text-[#71717A]">
              <th className="px-5 py-3 font-normal">When</th><th className="px-5 py-3 font-normal">Admin</th>
              <th className="px-5 py-3 font-normal">Action</th><th className="px-5 py-3 font-normal">Target</th>
              <th className="px-5 py-3 font-normal">IP</th>
            </tr></thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.log_id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid="admin-audit-row">
                  <td className="px-5 py-3 text-[#A1A1AA] whitespace-nowrap">{ago(l.created_at)}</td>
                  <td className="px-5 py-3 text-white">{l.admin_email || "—"}</td>
                  <td className="px-5 py-3"><span className="text-[#D4AF37]">{l.action}</span></td>
                  <td className="px-5 py-3 text-[#A1A1AA]">{l.target_type ? `${l.target_type}:${(l.target_id || "").slice(0, 16)}` : "—"}</td>
                  <td className="px-5 py-3 text-[#52525B]">{l.ip || "—"}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-[#71717A]">No audit entries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
